import React, {Dispatch, SetStateAction} from "react";
import {
    AllStates,
    asJsonformsErrorTranslate,
    asJsonformsTranslate,
    JSON_FORMS_CONFIG,
    StepForm,
    TranslateFunction
} from '../Forms';

import schema from './languages.schema.json';

import {JSONSchema7} from "json-schema";
import {JsonForms} from "@jsonforms/react";
import uischema from "./languages.uischema.json";
import {materialCells, materialRenderers} from "@jsonforms/material-renderers";
import {ErrorObject} from "ajv";
import {Languages} from "./Languages";
import {I18nMessage} from "../Representation";

export const EMPTY_LANGUAGES: Languages = {
    "english": {
        "pbt": {points: undefined},
        "ibt": {points: undefined},
        "bec_higher": {grade: undefined},
        "ielts": {result: undefined},
        "itp": {points: undefined},
        "fce": {grade: undefined},
        "cae": {grade: undefined},
        "pet": {grade: undefined},
        "bec_preliminary": {grade: undefined},
        "pearson": {points: undefined},
        "toeic": {writing: undefined, speaking: undefined, "listening and reading": undefined},
        "toeic_bridge": {points: undefined},
        "telc": {level: undefined},
        "unicert": {level: undefined},
        "igcse": {writing: undefined, reading: undefined, "listening and speaking": undefined},
        "abitur": {
            "b1": false
        },
        "other": {
            "name": undefined,
            "result": undefined,
            "b1": false
        }
    },
    "german": {
        "dsh": {level: undefined},
        "testdaf": {oral: undefined, reading: undefined, listening: undefined, written: undefined},
        "studienkolleg": {
            "passed": false
        },
        "dsdii": {oral: undefined, reading: undefined, listening: undefined, written: undefined},
        "gds": {
            "passed": false
        },
        "osd": {level: undefined},
        "telc": {
            "passed": false
        },
        "schule": {
            "equivalent": false
        },
        "hochschule": {
            "equivalent": false
        },
        "zeugnisse": {
            "equivalent": false
        }
    }
}

export enum LanguageLevels {
    INSUFFICIENT = 0,
    B1,
    C1
}

export function getEnglishLevel(languages: Languages) {
    const UNDEFINED = "UNDEFINED";

    switch (languages.english.certificate) {
        case "ielts": {
            const result = languages.english.ielts?.result ?? UNDEFINED;
            if (["C2", "C1", "9.0", "8.5", "8.0", "7.5", "7.0"].includes(result)) {
                return LanguageLevels.C1;
            } else if (["B2", "B1", "6.5", "6.0", "5.5", "5.0", "4.5", "4.0"].includes(result)) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "ibt": {
            const points = languages.english.ibt?.points ?? 0;
            if (points >= 95) {
                return LanguageLevels.C1;
            } else if (points >= 43) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "pbt": {
            const points = languages.english.pbt?.points ?? 0;
            if (points >= 627) {
                return LanguageLevels.C1;
            } else if (points >= 460) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "itp": {
            const points = languages.english.itp?.points ?? 0;
            if (points >= 627) {
                return LanguageLevels.C1;
            } else if (points >= 460) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "fce": {
            const grade = languages.english.fce?.grade ?? UNDEFINED;
            if (["Grade A / Distinction", "Level C1"].includes(grade)) {
                return LanguageLevels.C1;
            } else if (["Grade B / Merit", "Grade C / Pass", "Level B2", "Level B1"].includes(grade)) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "cae": {
            const grade = languages.english.cae?.grade ?? UNDEFINED;
            if (["Grade A / Distinction", "Grade B / Merit", "Grade C / Pass", "Level C1"].includes(grade)) {
                return LanguageLevels.C1;
            } else if (["Level B2", "Level B1"].includes(grade)) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "bec_higher": {
            const grade = languages.english.bec_higher?.grade ?? UNDEFINED;
            if (["Grade A / Distinction", "Grade B / Merit", "Grade C / Pass", "Level C1"].includes(grade)) {
                return LanguageLevels.C1;
            } else if (["Level B2", "Level B1"].includes(grade)) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "pet": {
            const grade = languages.english.pet?.grade ?? UNDEFINED;
            if (["Grade A / Distinction", "Grade B / Merit", "Grade C / Pass", "Level B2", "Level B1"].includes(grade)) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "bec_preliminary": {
            const grade = languages.english.bec_preliminary?.grade ?? UNDEFINED;
            if (["Grade A / Distinction", "Grade B / Merit", "Grade C / Pass", "Level B2", "Level B1"].includes(grade)) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "pearson": {
            const points = languages.english.pearson?.points ?? 0;
            if (points >= 76) {
                return LanguageLevels.C1;
            } else if (points >= 43) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "toeic": {
            const listeningReading = languages.english.toeic?.["listening and reading"] ?? 0;
            const speaking = languages.english.toeic?.speaking ?? 0;
            const writing = languages.english.toeic?.writing ?? 0;
            if (listeningReading >= 945 && speaking >= 180 && writing >= 180) {
                return LanguageLevels.C1;
            } else if (listeningReading >= 550 && speaking >= 120 && writing >= 120) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "toeic_bridge": {
            const points = languages.english.toeic_bridge?.points ?? 0;
            if (points >= 170) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "telc": {
            const level = languages.english.telc?.level ?? UNDEFINED;
            if (["telc C2 certificate", "telc C1 certificate"].includes(level)) {
                return LanguageLevels.C1;
            } else if (["telc B2 certificate", "telc B1 certificate"].includes(level)) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "unicert": {
            const level = languages.english.unicert?.level ?? UNDEFINED;
            if (["UNIcert IV", "UNIcert III"].includes(level)) {
                return LanguageLevels.C1;
            } else if (["UNIcert II", "UNIcert I"].includes(level)) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "igcse": {
            const listeningSpeaking = languages.english.igcse?.["listening and speaking"] ?? UNDEFINED;
            const reading = languages.english.igcse?.reading ?? UNDEFINED;
            const writing = languages.english.igcse?.writing ?? UNDEFINED;
            if (listeningSpeaking === UNDEFINED || reading === UNDEFINED || writing === UNDEFINED) {
                return LanguageLevels.INSUFFICIENT;
            } else {
                const levelsOrdered = ["A1", "A2", "B1", "B2", "C1", "C2"];
                const listeningSpeakingPoints = levelsOrdered.indexOf(listeningSpeaking);
                const readingPoints = levelsOrdered.indexOf(reading);
                const writingPoints = levelsOrdered.indexOf(writing);

                const average = Math.floor((listeningSpeakingPoints * 2 + readingPoints + writingPoints) / 4);
                if (average >= levelsOrdered.indexOf("C1")) {
                    return LanguageLevels.C1;
                } else if (average >= levelsOrdered.indexOf("B1")) {
                    return LanguageLevels.B1;
                } else {
                    return LanguageLevels.INSUFFICIENT;
                }
            }
        }
        case "abitur": {
            if (languages.english.abitur?.b1) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "other": {
            if (languages.english.other?.b1) {
                return LanguageLevels.B1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
    }
    return LanguageLevels.INSUFFICIENT;
}

export function getGermanLevel(languages: Languages): LanguageLevels {
    const UNDEFINED = "UNDEFINED";
    switch (languages.german.certificate) {
        case "dsh": {
            const level = languages.german.dsh?.level ?? UNDEFINED;
            if (["DSH-3", "DSH-2"].includes(level)) {
                return LanguageLevels.C1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "testdaf": {
            const readingLevel = languages.german.testdaf?.reading ?? UNDEFINED;
            const writingLevel = languages.german.testdaf?.written ?? UNDEFINED;
            const listeningLevel = languages.german.testdaf?.listening ?? UNDEFINED;
            const speakingLevel = languages.german.testdaf?.oral ?? UNDEFINED;
            const required = ["TDN 5", "TDN 4"];

            if (required.includes(readingLevel)
                && required.includes(writingLevel)
                && required.includes(listeningLevel)
                && required.includes(speakingLevel)) {
                return LanguageLevels.C1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "studienkolleg": {
            if (languages.german.studienkolleg?.passed) {
                return LanguageLevels.C1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "dsdii": {
            const readingLevel = languages.german.dsdii?.reading ?? UNDEFINED
            const writingLevel = languages.german.dsdii?.written ?? UNDEFINED
            const listeningLevel = languages.german.dsdii?.listening ?? UNDEFINED
            const speakingLevel = languages.german.dsdii?.oral ?? UNDEFINED

            const required = ["C2", "C1"];

            if (required.includes(readingLevel)
                && required.includes(writingLevel)
                && required.includes(listeningLevel)
                && required.includes(speakingLevel)) {
                return LanguageLevels.C1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "gds": {
            if (languages.german.gds?.passed) {
                return LanguageLevels.C1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "osd": {
            if (languages.german.osd?.level === "C2") {
                return LanguageLevels.C1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "telc": {
            if (languages.german.telc?.passed) {
                return LanguageLevels.C1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "schule": {
            if (languages.german.schule?.equivalent) {
                return LanguageLevels.C1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "hochschule": {
            if (languages.german.hochschule?.equivalent) {
                return LanguageLevels.C1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
        case "zeugnisse": {
            if (languages.german.zeugnisse?.equivalent) {
                return LanguageLevels.C1;
            } else {
                return LanguageLevels.INSUFFICIENT;
            }
        }
    }
    return LanguageLevels.INSUFFICIENT;
}

export function getLevelsString(languages: Languages) {
    const englishLevel = getEnglishLevel(languages);
    const germanLevel = getGermanLevel(languages);
    return {
        key: "language_levels", substitution: {
            english: englishLevel === LanguageLevels.INSUFFICIENT ? "< B1" : LanguageLevels[getEnglishLevel(languages)],
            german: germanLevel === LanguageLevels.INSUFFICIENT ? "< B1" : LanguageLevels[getGermanLevel(languages)]
        }
    };
}

const FORM_NS = "languages_form.";

export class LanguagesForm extends StepForm {

    constructor() {
        super(schema as JSONSchema7);
    }

    getFormNs(): string | undefined {
        return FORM_NS;
    }

    getTitleKey() {
        return "languages_title";
    };

    getPage([formData, setFormData]: [any, Dispatch<SetStateAction<any>>],  allStates: AllStates, t: TranslateFunction, lang: string) {
        return (<React.Fragment>
            <JsonForms
                schema={schema}
                uischema={uischema}
                data={formData}
                renderers={materialRenderers}
                cells={materialCells}
                config={JSON_FORMS_CONFIG}
                i18n={{
                    translate: asJsonformsTranslate(t, FORM_NS),
                    locale: lang,
                    translateError: asJsonformsErrorTranslate(t)
                }}
                onChange={({data}) => setFormData(data)}
            />
        </React.Fragment>);
    }

    getContextMessageKeys = (data: any, _allStates: AllStates): [I18nMessage[], I18nMessage[]] => {
        const languages = ((data as unknown) as Languages);
        if (languages === undefined) {
            return [["missing_data"], []];
        }

        const englishLevel = getEnglishLevel(languages);
        const germanLevel = getGermanLevel(languages);

        const levelsInfo = getLevelsString(languages);

        if ((englishLevel >= LanguageLevels.C1) ||
            (englishLevel >= LanguageLevels.B1 && germanLevel >= LanguageLevels.C1)) {
            // language requirements are met.
            return [[], [levelsInfo]];
        } else {
            if (englishLevel < LanguageLevels.B1) {
                return [["english_b1_required"], [levelsInfo]];
            } else {
                // B1 <= English < C1 (see condition above; if English would be at least C1, the first condition would have held
                // German must also be < C1, otherwise the first condition would have held
                return [["german_c1_required"], [levelsInfo]];
            }
        }
    }

    filterValidationErrors(data: any, errors: ErrorObject[] | null | undefined) {
        if (errors === null || errors === undefined) {
            return errors;
        }

        const languages = ((data as unknown) as Languages);
        if (languages === undefined) {
            return errors;
        }
        return errors.filter(error => {
            const path = error.instancePath;
            if (path.startsWith('/english') && error.params.missingProperty !== 'certificate') {
                return  path === '/english/' + languages.english.certificate || path.startsWith('/english/' + languages.english.certificate + '/');
            } else if (path.startsWith('/german')) {
                if (path === '/german' && error.params.missingProperty === 'certificate') {
                    return true;
                } else {
                    return path.startsWith('/german/' + languages.german.certificate);
                }
            }
            return true;
        });
    }

    getExplanationMarkdownKey() {
        return "languages.explanation.markdown";
    }

    getHelpUrl(): string {
        return "faq.html#languages";
    }

}